import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@e2n/ui/v2';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AbgleichZeile } from '../../../hooks/useMitarbeiterabgleich/entities/Mitarbeiterabgleich';
import { EinzelnZuordnung } from './EinzelnZuordnung';
import { FuerAlleZuordnung } from './FuerAlleZuordnung';

type Modus = 'alle' | 'einzeln';

function ZuordnungInhalt({
  zeilen,
  standorte,
  onFertig,
}: {
  zeilen: AbgleichZeile[];
  standorte: string[];
  onFertig: (zuordnung: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const [modus, setModus] = useState<Modus>('alle');
  const [standortFuerAlle, setStandortFuerAlle] = useState<string>();
  const [einzeln, setEinzeln] = useState<Record<string, string>>({});

  const zuordnung: Record<string, string> =
    modus === 'alle'
      ? Object.fromEntries(
          standortFuerAlle ? zeilen.map((z) => [z.id, standortFuerAlle]) : [],
        )
      : einzeln;
  const vollstaendig = zeilen.every((z) => zuordnung[z.id]);

  const fuerAlle = (
    <FuerAlleZuordnung
      zeilen={zeilen}
      standorte={standorte}
      standort={standortFuerAlle}
      onStandortChange={setStandortFuerAlle}
    />
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t('domain.datev.abgleich.standorteZuordnen')}
        </DialogTitle>
        {zeilen.length > 1 ? (
          <Tabs
            value={modus}
            onValueChange={(value) => setModus(value as Modus)}
            className="w-full gap-6">
            <TabsList className="self-start">
              <TabsTrigger value="alle">
                {t('domain.datev.abgleich.zuordnung.fuerAlle')}
              </TabsTrigger>
              <TabsTrigger value="einzeln">
                {t('domain.datev.abgleich.zuordnung.einzeln')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="alle">{fuerAlle}</TabsContent>
            <TabsContent value="einzeln">
              <EinzelnZuordnung
                zeilen={zeilen}
                standorte={standorte}
                zuordnung={einzeln}
                onZuordnungChange={(id, standort) =>
                  setEinzeln((prev) => ({ ...prev, [id]: standort }))
                }
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="w-full">{fuerAlle}</div>
        )}
      </DialogHeader>
      <ZuordnungFooter
        disabled={!vollstaendig}
        onFertig={() => onFertig(zuordnung)}
      />
    </>
  );
}

function ZuordnungFooter({
  disabled,
  onFertig,
}: {
  disabled: boolean;
  onFertig: () => void;
}) {
  const { t } = useTranslation();

  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button variant="secondary-blue">
          {t('domain.datev.abgleich.zuordnung.abbrechen')}
        </Button>
      </DialogClose>
      <Button disabled={disabled} onClick={onFertig}>
        {t('domain.datev.abgleich.zuordnung.fertig')}
      </Button>
    </DialogFooter>
  );
}

export function StandortZuordnungDialog({
  zeilen,
  standorte,
  onOpenChange,
  onFertig,
}: {
  zeilen: AbgleichZeile[] | null;
  standorte: string[];
  onOpenChange: (open: boolean) => void;
  onFertig: (zuordnung: Record<string, string>) => void;
}) {
  return (
    <Dialog open={zeilen !== null} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        {zeilen && (
          <ZuordnungInhalt
            zeilen={zeilen}
            standorte={standorte}
            onFertig={onFertig}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
